<template>
  <div class="ai-chat-container">
    <!-- 左侧历史会话列表 -->
    <div class="session-sidebar">
      <div class="sidebar-header">
        <a-button type="primary" icon="plus" block class="new-chat-btn" @click="createSession">新建对话</a-button>
      </div>
      <div class="session-list">
        <div v-if="sessions.length === 0" class="empty-sessions">
          <a-icon type="message" style="font-size: 32px; color: #d9d9d9;" />
          <p>暂无历史对话</p>
          <p class="empty-hint">点击"新建对话"开始使用AI助手</p>
        </div>
        <div
          v-for="(session, index) in sessions"
          :key="session.sessionId"
          :class="['session-item', session.sessionId === sessionId ? 'session-active' : '']"
          @click="selectSession(session)"
        >
          <div class="session-icon">
            <a-icon type="message" />
          </div>
          <div class="session-info">
            <div class="session-title">对话 {{ sessions.length - index }}</div>
            <div class="session-time">{{ session.createTime }}</div>
          </div>
        </div>
      </div>
      <div class="sidebar-footer">
        <div class="footer-tip">
          <a-icon type="info-circle" style="margin-right: 4px;" />
          AI助手可能出错，请核实重要信息
        </div>
      </div>
    </div>

    <!-- 右侧聊天区域 -->
    <div class="chat-main">
      <!-- 顶部栏 -->
      <div class="chat-header">
        <div class="header-left">
          <a-icon type="robot" class="header-icon" />
          <span class="header-title">AI智能运营助手</span>
          <a-tag v-if="sessionId" color="green" style="margin-left: 12px;">在线</a-tag>
        </div>
        <div class="header-right" v-if="sessionId">
          <span class="session-id-label">会话: {{ sessionId.substring(0, 8) }}...</span>
        </div>
      </div>

      <!-- 聊天区域 -->
      <div class="chat-body" ref="chatBody">
        <!-- 无会话时的引导页 -->
        <div v-if="!sessionId" class="empty-guide">
          <div class="guide-icon">
            <a-icon type="robot" />
          </div>
          <h2>欢迎使用AI智能运营助手</h2>
          <p class="guide-desc">我可以帮您快速完成商品管理、套餐创建、数据分析等操作</p>
          <div class="guide-features">
            <div class="feature-item">
              <a-icon type="shopping-cart" class="feature-icon" />
              <div class="feature-text">
                <strong>商品管理</strong>
                <span>上架、修改价格、库存调整</span>
              </div>
            </div>
            <div class="feature-item">
              <a-icon type="gift" class="feature-icon" />
              <div class="feature-text">
                <strong>套餐管理</strong>
                <span>创建套餐、配置优惠</span>
              </div>
            </div>
            <div class="feature-item">
              <a-icon type="bar-chart" class="feature-icon" />
              <div class="feature-text">
                <strong>数据分析</strong>
                <span>销售报表、趋势分析</span>
              </div>
            </div>
          </div>
          <a-button type="primary" size="large" icon="plus" @click="createSession" class="guide-start-btn">
            开始新对话
          </a-button>
        </div>

        <!-- 有会话时的消息列表 -->
        <template v-if="sessionId">
          <!-- 无消息时显示可选操作卡片 -->
          <div v-if="messages.length === 0 && availableFunctions.length > 0" class="operation-guide">
            <div class="guide-title">请选择您要执行的操作：</div>
            <div class="operation-cards">
              <div
                v-for="(op, index) in availableFunctions"
                :key="index"
                class="operation-card"
                @click="selectOperation(op)"
              >
                <div class="op-icon">
                  <a-icon :type="op.icon" />
                </div>
                <div class="op-info">
                  <div class="op-name">{{ op.functionDesc }}</div>
                  <div class="op-hint">点击开始</div>
                </div>
                <a-icon type="right" class="op-arrow" />
              </div>
            </div>
            <div class="guide-or">— 或直接输入您的需求 —</div>
          </div>
        
          <!-- 无操作卡片时的空状态 -->
          <div v-if="messages.length === 0 && availableFunctions.length === 0" class="empty-chat">
            <a-icon type="message" style="font-size: 40px; color: #e0e0e0;" />
            <p>开始对话吧！描述您的需求，AI助手会帮您完成操作</p>
            <p class="empty-hint">例如：“帮我创建一个9.9元的游戏币套餐”</p>
          </div>

          <div
            v-for="(msg, index) in messages"
            :key="index"
            :class="['message-item', msg.role === 'user' ? 'message-user' : 'message-ai']"
          >
            <div class="message-avatar">
              <div :class="['avatar-circle', msg.role === 'user' ? 'avatar-user' : 'avatar-ai']">
                <a-icon :type="msg.role === 'user' ? 'user' : 'robot'" />
              </div>
            </div>
            <div class="message-body">
              <div class="message-header">
                <span class="message-name">{{ msg.role === 'user' ? '我' : 'AI助手' }}</span>
                <span class="message-time" v-if="msg.time">{{ msg.time }}</span>
              </div>
              <div :class="['message-bubble', msg.role === 'user' ? 'bubble-user' : 'bubble-ai']">
                <div class="message-text" v-html="formatMessage(msg.content)"></div>
              </div>

              <!-- 待确认操作卡片 -->
              <div v-if="msg.pendingFunction && pendingAction && pendingAction.functionName === msg.pendingFunction" class="confirm-card">
                <div class="confirm-header">
                  <a-icon type="safety-certificate" class="confirm-icon" />
                  <span class="confirm-title">待确认操作</span>
                  <a-tag color="blue" style="margin-left: 8px;">{{ pendingAction.functionName }}</a-tag>
                </div>
                <div class="confirm-params">
                  <div class="params-label">参数详情：</div>
                  <div class="params-table">
                    <div v-for="(value, key) in parseParams(pendingAction.arguments)" :key="key" class="param-row">
                      <span class="param-key">{{ key }}</span>
                      <span class="param-value">{{ formatParamValue(value) }}</span>
                    </div>
                  </div>
                </div>
                <div class="confirm-actions">
                  <a-button type="primary" size="small" @click="confirmExecute" :loading="executing" icon="check">
                    确认执行
                  </a-button>
                  <a-button size="small" @click="modifyParams" icon="edit">
                    修改参数
                  </a-button>
                  <a-button size="small" type="danger" ghost @click="cancelExecute" icon="close">
                    取消
                  </a-button>
                </div>
              </div>

              <!-- 已执行结果卡片 -->
              <div v-if="msg.pendingFunction && msg.executeResult" class="result-card" :class="msg.executeResult.success ? 'result-success' : 'result-fail'">
                <a-icon :type="msg.executeResult.success ? 'check-circle' : 'close-circle'" class="result-icon" />
                <div class="result-content">
                  <div class="result-title">{{ msg.executeResult.success ? '执行成功' : '执行失败' }}</div>
                  <div class="result-desc">{{ msg.executeResult.info || msg.executeResult.message || (msg.executeResult.success ? '操作已完成' : '请稍后重试') }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- AI正在回复 -->
          <div v-if="loading" class="message-item message-ai">
            <div class="message-avatar">
              <div class="avatar-circle avatar-ai">
                <a-icon type="robot" />
              </div>
            </div>
            <div class="message-body">
              <div class="message-header">
                <span class="message-name">AI助手</span>
              </div>
              <div class="message-bubble bubble-ai">
                <div class="typing-indicator">
                  <span class="typing-dot"></span>
                  <span class="typing-dot"></span>
                  <span class="typing-dot"></span>
                  <span class="typing-text">正在思考中...</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- 输入区域 -->
      <div class="chat-footer" v-if="sessionId">
        <div class="input-wrapper">
          <a-textarea
            v-model="inputText"
            placeholder="描述您的需求，例如：'上架一个9.9元的游戏币套餐'... (Enter发送，Shift+Enter换行)"
            :auto-size="{ minRows: 2, maxRows: 5 }"
            @keydown="handleKeydown"
            :disabled="loading"
            class="chat-input"
          />
          <div class="input-actions">
            <span class="input-hint">
              <a-icon type="enter" style="margin-right: 4px;" />
              Enter 发送 / Shift+Enter 换行
            </span>
            <a-button
              type="primary"
              icon="send"
              :disabled="!inputText.trim() || loading"
              @click="sendMessage"
              :loading="loading"
            >
              发送
            </a-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sendAiChat, createAiSession, getAiHistory, getAiSessions, executeAiFunction } from "@/api/storeApi.js";
import { message } from "ant-design-vue";

export default {
  data() {
    return {
      sessionId: "",
      messages: [],
      inputText: "",
      loading: false,
      sessions: [],
      pendingAction: null,
      executing: false,
      availableFunctions: []
    };
  },
  mounted() {
    this.loadSessions();
  },
  methods: {
    loadSessions() {
      getAiSessions().then(res => {
        if (res && res.data) {
          this.sessions = res.data;
        }
      }).catch(() => {});
    },
    selectSession(session) {
      if (this.sessionId === session.sessionId) return;
      this.sessionId = session.sessionId;
      this.messages = [];
      this.pendingAction = null;
      this.availableFunctions = [];
      this.loadHistory(session.sessionId);
    },
    loadHistory(sessionId) {
      getAiHistory(sessionId).then(res => {
        if (res && res.data) {
          this.messages = res.data
            .filter(msg => msg.role === 'user' || msg.role === 'assistant')
            .map(msg => ({ role: msg.role, content: msg.content, time: this.getCurrentTime() }));
        }
        this.scrollToBottom();
      }).catch(() => {});
    },
    createSession() {
      createAiSession('default').then(res => {
        if (res && res.data) {
          // 兼容新旧两种返回格式
          if (typeof res.data === 'object' && res.data.sessionId) {
            this.sessionId = res.data.sessionId;
            this.availableFunctions = res.data.availableFunctions || [];
          } else {
            this.sessionId = res.data;
            this.availableFunctions = [];
          }
          this.messages = [];
          this.pendingAction = null;
          message.success("新对话已创建");
          this.loadSessions();
        }
      }).catch(() => {
        message.error("创建对话失败");
      });
    },
    selectOperation(op) {
      // 隐藏操作卡片，发送预置提示词给AI
      this.availableFunctions = [];
      this.inputText = op.prompt;
      this.sendMessage();
    },
    sendMessage() {
      const text = this.inputText.trim();
      if (!text) return;
      this.messages.push({ role: "user", content: text, time: this.getCurrentTime() });
      this.inputText = "";
      this.loading = true;
      this.pendingAction = null;
      this.scrollToBottom();

      sendAiChat({
        sessionId: this.sessionId,
        message: text,
        scenario: "default"
      }).then(res => {
        this.loading = false;
        if (res && res.data) {
          if (res.data.sessionId) {
            this.sessionId = res.data.sessionId;
          }
          if (res.data.pendingFunction) {
            this.pendingAction = {
              functionName: res.data.pendingFunction,
              arguments: res.data.pendingArguments
            };
            this.messages.push({
              role: "assistant",
              content: res.data.reply,
              pendingFunction: res.data.pendingFunction,
              time: this.getCurrentTime()
            });
          } else if (res.data.reply) {
            this.messages.push({ role: "assistant", content: res.data.reply, time: this.getCurrentTime() });
          } else if (res.data.errorMsg) {
            message.error(res.data.errorMsg);
          }
        }
        this.scrollToBottom();
      }).catch(() => {
        this.loading = false;
        message.error("发送失败，请重试");
      });
    },
    confirmExecute() {
      if (!this.pendingAction) return;
      this.executing = true;
      executeAiFunction({
        functionName: this.pendingAction.functionName,
        arguments: this.pendingAction.arguments
      }).then(res => {
        this.executing = false;
        const resultMsg = this.messages.find(m => m.pendingFunction === this.pendingAction.functionName);
        if (resultMsg) {
          resultMsg.executeResult = res || { success: false, info: '执行失败' };
        }
        if (res && res.success) {
          message.success(res.info || '执行成功');
          // 执行成功后，让AI回复确认
          this.messages.push({
            role: "assistant",
            content: "操作已成功执行！如需继续其他操作，请告诉我。",
            time: this.getCurrentTime()
          });
        } else {
          const errorMsg = res ? (res.info || '执行失败') : '执行失败';
          this.messages.push({
            role: "assistant",
            content: "操作执行失败：" + errorMsg + "\n\n您可以告诉我需要修改的内容，我来帮您调整。",
            time: this.getCurrentTime()
          });
        }
        this.pendingAction = null;
        this.scrollToBottom();
      }).catch(() => {
        this.executing = false;
        message.error('执行失败，请重试');
      });
    },
    cancelExecute() {
      this.pendingAction = null;
      this.messages.push({
        role: "assistant",
        content: "已取消操作。如果您需要修改参数或执行其他操作，请告诉我。",
        time: this.getCurrentTime()
      });
      this.scrollToBottom();
    },
    modifyParams() {
      this.pendingAction = null;
      this.$nextTick(() => {
        this.inputText = '我想修改参数：';
        this.$nextTick(() => {
          const textarea = document.querySelector('.chat-input textarea');
          if (textarea) {
            textarea.focus();
          }
        });
      });
    },
    parseParams(argsStr) {
      try {
        return JSON.parse(argsStr);
      } catch (e) {
        return {};
      }
    },
    formatParamValue(value) {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'object') return JSON.stringify(value, null, 2);
      return String(value);
    },
    getCurrentTime() {
      const now = new Date();
      return now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
    },
    handleKeydown(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },
    formatMessage(content) {
      if (!content) return "";
      // 简单的格式化：换行、加粗、列表
      let html = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/`(.*?)`/g, '<code>$1</code>');
      // 列表项
      html = html.replace(/(?:^|<br\/>)\s*[-•]\s+(.*?)(?=<br\/>|$)/g, '<br/><span class="list-item">• $1</span>');
      return html;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        var body = this.$refs.chatBody;
        if (body) {
          body.scrollTop = body.scrollHeight;
        }
      });
    }
  }
};
</script>

<style scoped>
/* ========== 整体布局 ========== */
.ai-chat-container {
  display: flex;
  height: calc(100vh - 140px);
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

/* ========== 左侧会话列表 ========== */
.session-sidebar {
  width: 280px;
  min-width: 280px;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  background: #fafbfc;
}

.sidebar-header {
  padding: 20px 16px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.new-chat-btn {
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
}

.session-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.session-list::-webkit-scrollbar {
  width: 4px;
}

.session-list::-webkit-scrollbar-thumb {
  background: #d9d9d9;
  border-radius: 4px;
}

.empty-sessions {
  text-align: center;
  color: #999;
  padding: 60px 20px;
}

.empty-sessions p {
  margin: 8px 0 0;
  font-size: 14px;
}

.empty-hint {
  font-size: 12px !important;
  color: #bbb !important;
  margin-top: 4px !important;
}

.session-item {
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 2px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.session-item:hover {
  background: #e6f7ff;
}

.session-active {
  background: #e6f7ff;
  border-left: 3px solid #1890ff;
}

.session-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #e6f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1890ff;
  font-size: 14px;
  flex-shrink: 0;
}

.session-active .session-icon {
  background: #bae7ff;
}

.session-info {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
}

.session-title {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-time {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

.sidebar-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
}

.footer-tip {
  font-size: 11px;
  color: #999;
  text-align: center;
}

/* ========== 右侧聊天主区域 ========== */
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #fff;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-icon {
  font-size: 22px;
  color: #1890ff;
  margin-right: 10px;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.session-id-label {
  font-size: 12px;
  color: #999;
  font-family: monospace;
}

/* ========== 聊天区域 ========== */
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #f7f8fa;
}

.chat-body::-webkit-scrollbar {
  width: 6px;
}

.chat-body::-webkit-scrollbar-thumb {
  background: #d9d9d9;
  border-radius: 6px;
}

/* 无会话引导页 */
.empty-guide {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 40px;
}

.guide-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e6f7ff, #bae7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.guide-icon .anticon {
  font-size: 36px;
  color: #1890ff;
}

.empty-guide h2 {
  font-size: 22px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.guide-desc {
  font-size: 14px;
  color: #666;
  margin-bottom: 32px;
}

.guide-features {
  display: flex;
  gap: 24px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  justify-content: center;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  width: 200px;
  text-align: left;
}

.feature-icon {
  font-size: 24px;
  color: #1890ff;
}

.feature-text {
  display: flex;
  flex-direction: column;
}

.feature-text strong {
  font-size: 14px;
  color: #333;
}

.feature-text span {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.guide-start-btn {
  height: 44px;
  padding: 0 32px;
  font-size: 15px;
  border-radius: 22px;
}

/* 空聊天 */
.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
}

/* ========== 操作引导卡片 ========== */
.operation-guide {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px 20px;
}

.guide-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.operation-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  max-width: 600px;
}

.operation-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.operation-card:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
  transform: translateY(-1px);
}

.op-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e6f7ff, #bae7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.op-icon .anticon {
  font-size: 18px;
  color: #1890ff;
}

.op-info {
  flex: 1;
  min-width: 0;
}

.op-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.op-hint {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.op-arrow {
  color: #d9d9d9;
  font-size: 14px;
  flex-shrink: 0;
}

.guide-or {
  margin-top: 24px;
  font-size: 13px;
  color: #bbb;
}

.empty-chat p {
  margin-top: 12px;
  font-size: 14px;
}

/* ========== 消息项 ========== */
.message-item {
  display: flex;
  margin-bottom: 24px;
  animation: fadeInUp 0.3s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-user {
  flex-direction: row-reverse;
}

.message-avatar {
  flex-shrink: 0;
  margin: 0 12px;
}

.avatar-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.avatar-user {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: #fff;
}

.avatar-ai {
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: #fff;
}

.message-body {
  max-width: 70%;
  display: flex;
  flex-direction: column;
}

.message-user .message-body {
  align-items: flex-end;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.message-name {
  font-size: 13px;
  font-weight: 500;
  color: #555;
}

.message-time {
  font-size: 11px;
  color: #bbb;
}

/* ========== 消息气泡 ========== */
.message-bubble {
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.7;
  word-break: break-word;
}

.bubble-user {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: #fff;
  border-top-right-radius: 4px;
}

.bubble-ai {
  background: #fff;
  color: #333;
  border-top-left-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.message-text :deep(code) {
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  font-family: 'SFMono-Regular', Consolas, monospace;
}

.message-text :deep(.list-item) {
  display: block;
  padding-left: 4px;
  line-height: 2;
}

.bubble-user .message-text :deep(code) {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* ========== 打字动画 ========== */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.typing-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #999;
  animation: typingBounce 1.4s infinite ease-in-out;
}

.typing-dot:nth-child(1) { animation-delay: 0s; }
.typing-dot:nth-child(2) { animation-delay: 0.2s; }
.typing-dot:nth-child(3) { animation-delay: 0.4s; }

.typing-text {
  margin-left: 8px;
  font-size: 13px;
  color: #999;
}

@keyframes typingBounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-4px); opacity: 1; }
}

/* ========== 确认卡片 ========== */
.confirm-card {
  margin-top: 10px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.confirm-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #e6f7ff, #f0f5ff);
  border-bottom: 1px solid #e8e8e8;
}

.confirm-icon {
  font-size: 18px;
  color: #1890ff;
}

.confirm-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-left: 8px;
}

.confirm-params {
  padding: 12px 16px;
  max-height: 240px;
  overflow-y: auto;
}

.params-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
}

.params-table {
  background: #f9fafb;
  border-radius: 8px;
  padding: 8px 12px;
}

.param-row {
  display: flex;
  align-items: flex-start;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid #f0f0f0;
}

.param-row:last-child {
  border-bottom: none;
}

.param-key {
  color: #1890ff;
  font-weight: 500;
  min-width: 100px;
  flex-shrink: 0;
}

.param-key::after {
  content: "：";
}

.param-value {
  color: #333;
  word-break: break-all;
  white-space: pre-wrap;
}

.confirm-actions {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

/* ========== 执行结果卡片 ========== */
.result-card {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
}

.result-success {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}

.result-fail {
  background: #fff2f0;
  border: 1px solid #ffccc7;
}

.result-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.result-success .result-icon {
  color: #52c41a;
}

.result-fail .result-icon {
  color: #ff4d4f;
}

.result-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.result-desc {
  font-size: 13px;
  color: #666;
  margin-top: 2px;
}

/* ========== 输入区域 ========== */
.chat-footer {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
}

.input-wrapper {
  background: #f7f8fa;
  border-radius: 12px;
  padding: 12px 16px;
  border: 1px solid #e8e8e8;
  transition: border-color 0.2s;
}

.input-wrapper:focus-within {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
}

.chat-input {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  font-size: 14px;
  resize: none !important;
}

.chat-input:focus {
  box-shadow: none !important;
}

.input-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.input-hint {
  font-size: 12px;
  color: #bbb;
  display: flex;
  align-items: center;
}

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .session-sidebar {
    width: 60px;
    min-width: 60px;
  }
  .sidebar-header {
    padding: 12px 8px;
  }
  .new-chat-btn span {
    display: none;
  }
  .session-info, .sidebar-footer, .empty-sessions p {
    display: none;
  }
  .session-item {
    justify-content: center;
    padding: 8px;
  }
  .session-icon {
    margin: 0;
  }
  .message-body {
    max-width: 85%;
  }
  .guide-features {
    flex-direction: column;
    align-items: center;
  }
  .operation-cards {
    flex-direction: column;
    align-items: center;
  }
  .operation-card {
    min-width: 260px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/systemManagement/aiChat/aiChat.vue